import type { HeartRateEvent } from '@model/DeviceConfiguration';
import {
  detectHrDataIssues,
  normalizeTimestampToSecond,
} from './detectHrDataIssues';
import type { AiRepairOperations } from './types';

const cloneEvent = (event: HeartRateEvent): HeartRateEvent => ({
  ...event,
  id: undefined,
});

export const applyAiRepairs = (
  originalEvents: HeartRateEvent[],
  operations: AiRepairOperations
): {
  repairedEvents: HeartRateEvent[];
  repairedOnly: HeartRateEvent[];
  duplicateMerges: number;
  gapFillCount: number;
} => {
  const issues = detectHrDataIssues(originalEvents);
  const duplicateSeconds = new Set(
    issues.duplicates.map(item => item.secondTimestamp)
  );
  const duplicateOps = new Map(
    operations.duplicateMerges.map(item => [item.secondTimestamp, item])
  );

  const grouped = new Map<number, HeartRateEvent[]>();
  originalEvents.forEach(event => {
    if (typeof event.timestamp !== 'number') return;
    const second = normalizeTimestampToSecond(event.timestamp);
    const current = grouped.get(second) ?? [];
    current.push(event);
    grouped.set(second, current);
  });

  const output: HeartRateEvent[] = [];
  const repairedOnly: HeartRateEvent[] = [];

  Array.from(grouped.entries())
    .sort((left, right) => left[0] - right[0])
    .forEach(([secondTimestamp, groupEvents]) => {
      if (duplicateSeconds.has(secondTimestamp)) {
        const operation = duplicateOps.get(secondTimestamp);
        if (!operation) {
          throw new Error(
            `Missing duplicate merge operation for second ${secondTimestamp}`
          );
        }

        const mergedEvent: HeartRateEvent = {
          timestamp: secondTimestamp,
          deviceCode: operation.merged.deviceCode ?? groupEvents[0]?.deviceCode,
          hr: operation.merged.hr,
          hrv: operation.merged.hrv,
          lf: operation.merged.lf,
          hf: operation.merged.hf,
          repair: {
            type: 'duplicate',
            sourceCount: groupEvents.length,
            summary: `Uniti ${groupEvents.length} eventi duplicati nello stesso secondo`,
          },
        };
        output.push(mergedEvent);
        repairedOnly.push(mergedEvent);
        return;
      }

      if (groupEvents.length !== 1) return;
      output.push(cloneEvent(groupEvents[0]));
    });

  operations.gapInsertions.forEach(insertion => {
    const gapEvent: HeartRateEvent = {
      timestamp: insertion.timestamp,
      hr: insertion.hr,
      hrv: insertion.hrv,
      lf: insertion.lf,
      hf: insertion.hf,
      type: 'gap',
      repair: {
        type: 'gap',
        fromTimestamp: insertion.fromTimestamp,
        toTimestamp: insertion.toTimestamp,
        summary: `Colmato gap tra ${insertion.fromTimestamp} e ${insertion.toTimestamp}`,
      },
    };
    output.push(gapEvent);
    repairedOnly.push(gapEvent);
  });

  const repairedEvents = output.sort(
    (left, right) => left.timestamp - right.timestamp
  );

  return {
    repairedEvents,
    repairedOnly,
    duplicateMerges: operations.duplicateMerges.length,
    gapFillCount: operations.gapInsertions.length,
  };
};
